import { useParams } from 'react-router-dom';
import { RiskPlusPageShell, SectionCard, RiskBadge, riskColor, BulletList, NumberedItem } from './RiskPlusPageShell';
import { useRiskPlusPageData } from './useRiskPlusPageData';
import type { RiskPlusReport } from '../../entities/report/RiskPlusReport';

function SystemOverview({ report }: { report: RiskPlusReport }) {
  const { useCaseSummary, riskDrivers, intendedPurposes, intendedUsers } = report.useCaseOverview;

  return (
    <SectionCard title="System Overview">
      {useCaseSummary && (
        <p className="text-xs leading-relaxed text-midnight-navi">{useCaseSummary}</p>
      )}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {intendedPurposes.length > 0 && (
          <div>
            <div className="text-xxs font-semibold uppercase tracking-wider text-gray-500">
              Intended Purposes
            </div>
            <div className="mt-2">
              <BulletList items={intendedPurposes} />
            </div>
          </div>
        )}
        {intendedUsers.length > 0 && (
          <div>
            <div className="text-xxs font-semibold uppercase tracking-wider text-gray-500">
              Intended Users
            </div>
            <div className="mt-2">
              <BulletList items={intendedUsers} />
            </div>
          </div>
        )}
      </div>

      {riskDrivers.length > 0 && (
        <div className="mt-4">
          <div className="text-xxs font-semibold uppercase tracking-wider text-gray-400">
            Why Risk Mitigation is Required
          </div>
          <ul className="mt-2 space-y-1.5">
            {riskDrivers.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-midnight-navi">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                <span>
                  <span className="font-medium">{d.label}</span>
                  {d.description && d.description !== d.label && (
                    <span className="text-gray-500"> - {d.description}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}

function PreconditionsCard({ report }: { report: RiskPlusReport }) {
  const reqs = report.leadershipSummary.deploymentRequirements;
  if (reqs.length === 0) return null;

  return (
    <SectionCard title="Minimum Preconditions Before Deployment">
      <div className="space-y-3">
        {reqs.map((req, i) => (
          <NumberedItem key={i} index={i} title={req.label}>
            {req.requirement && (
              <div className="mt-1">
                <BulletList
                  items={req.requirement.split(';').map((s) => s.trim()).filter(Boolean)}
                />
              </div>
            )}
          </NumberedItem>
        ))}
      </div>
    </SectionCard>
  );
}

function KeyRisksSection({ report }: { report: RiskPlusReport }) {
  const { riskRegister } = report;
  if (riskRegister.length === 0) return null;

  return (
    <SectionCard title="Summary of Identified Risks">
      <div className="summary-note">
        <p>
          This report details the risks rated <strong>Critical</strong> and <strong>High</strong> for this deployment.
          Additional risks rated{" "}
          <span className="badge medium" aria-hidden="true" /> <strong>Moderate</strong> and{" "}
          <span className="badge low" aria-hidden="true" /> <strong>Low</strong> have been identified but are not
          included in this report.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {riskRegister.map((risk) => (
          <div
            key={risk.id}
            className="rounded-lg border border-gray-200 bg-soft-gray px-4 py-3"
            style={{ borderLeftWidth: 3, borderLeftColor: riskColor(risk.riskRank) }}
          >
            <div className="flex items-start justify-between gap-2">
              <RiskBadge rank={risk.riskRank} />
              <span className="shrink-0 rounded bg-soft-gray px-1.5 py-0.5 text-xxs font-mono text-gray-500">
                {risk.id}
              </span>
            </div>
            <div className="mt-2 text-xs font-semibold text-midnight-navi leading-snug">{risk.label}</div>
            {risk.riskScenarioDescription && (
              <div className="mt-1 text-xxs text-gray-500">{risk.riskScenarioDescription}</div>
            )}
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

const RiskPlusExecutiveSummary = () => {
  const { useCaseChatId = '' } = useParams<{ useCaseChatId: string }>();
  const data = useRiskPlusPageData(useCaseChatId);

  return (
    <RiskPlusPageShell useCaseChatId={useCaseChatId} data={data}>
      {(report) => (
        <>
          <SystemOverview report={report} />
          <KeyRisksSection report={report} />
          <PreconditionsCard report={report} />
        </>
      )}
    </RiskPlusPageShell>
  );
};

export default RiskPlusExecutiveSummary;
